import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import Refresh from '@mui/icons-material/Refresh';
import { Divider } from '@mui/material';
import { createSelector } from 'reselect';

import { hasFreeSubscription, UpgradeMenuItems } from '@paragon/login-tools';

import { useAppMonitor } from '../../../../monitor';
import {
  deleteAllCarriedComponentConnectionsOnActiveComponent,
  deleteJackGirderConnections,
} from '../../../../store/actions/deletionActions';
import { regenerateConnectionsOnEnvelopes } from '../../../../store/actions/layout/trussEnvelopeActions';
import { showUpgradeAccountDialog } from '../../../../store/actions/popupActions';
import { getActiveComponentDesignGuid } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getJackGirderConnections } from '../../../../store/selectors/getJackGirderConnections';
import { getAllTrussEnvelopes } from '../../../../store/selectors/layoutDataSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { AddItemMenuItem } from '../shared-menu-items/AddItemMenuItem';

const getCustomCarriedComponentConnections = createSelector(
  getActiveComponentDesignGuid,
  getJackGirderConnections,

  (activeComponentDesignGuid, jackGirderConnections) =>
    activeComponentDesignGuid
      ? jackGirderConnections.filter(
          (connection) => connection.girderGuid === activeComponentDesignGuid && connection.userCreated,
        )
      : [],
);

export const getRelevantTrussEnvelopes = createSelector(
  getAllTrussEnvelopes,
  getActiveComponentDesignGuid,
  (trussEnvelopes, activeComponentDesignGuid) =>
    activeComponentDesignGuid
      ? trussEnvelopes.filter((envelope) => envelope.componentDesignGuid === activeComponentDesignGuid)
      : [],
);

export function JackGirderConnectionsContainerMenu() {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const isFreeUser = useAppSelector(hasFreeSubscription);

  const containingEnvelopes = useAppSelector(getRelevantTrussEnvelopes);
  const customConnections = useAppSelector(getCustomCarriedComponentConnections);

  return (
    <SceneMenuContent>
      <UpgradeMenuItems
        active={isFreeUser}
        briefDescription="create carried components"
        onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
      >
        <AddItemMenuItem type="jackGirderConnection" userVisibleTerm="Carried Component" disabled={isFreeUser} />
      </UpgradeMenuItems>

      <Divider />

      <SceneMenuItem
        icon={<Refresh />}
        text="Regenerate Connections"
        disabled={containingEnvelopes.length === 0}
        onClick={() => {
          dispatch(regenerateConnectionsOnEnvelopes(containingEnvelopes));
          monitor.recordInteraction('Regenerate Carried Component Connections menu item clicked');
        }}
      />

      <Divider />

      <SceneMenuItem
        icon={<DeleteIcon />}
        text="Delete Custom Connections"
        disabled={customConnections.length === 0}
        onClick={() => {
          dispatch(deleteJackGirderConnections(customConnections));
          monitor.recordInteraction('Delete Custom Carried Component Connections menu item clicked');
        }}
      />

      <SceneMenuItem
        icon={<DeleteIcon />}
        text="Delete All Connections"
        onClick={() => {
          dispatch(deleteAllCarriedComponentConnectionsOnActiveComponent());
          monitor.recordInteraction('Delete All Carried Component Connections menu item clicked');
        }}
      />
    </SceneMenuContent>
  );
}
